import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Gift, Search, Copy, X, Clock, CheckCircle2, Ban, RotateCcw, StickyNote } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import { REFERRAL_STATUS, actionsFor, eventLine, referralProgress } from "../../lib/referralAdmin";

/**
 * The referral codes somebody enrolled with (13.10.2026). Everybody who pays
 * for their own place has a code, so the list starts with those that brought
 * at least one colleague; the rest are one switch away.
 *
 * In a code's drawer the team marks the benefit as given, withdraws a code,
 * takes either back and writes notes (`services/referrals/desk.py`). The
 * buttons show only for who the server lets act: owner and admin.
 */
export default function ReferralsTab({ canMutate = false }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(""); // "" = all
  const [q, setQ] = useState("");
  const [unused, setUnused] = useState(false);
  const [drawerId, setDrawerId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { used: !unused };
      if (filter) params.status = filter;
      if (q) params.q = q;
      const r = await api.get("/admin/referrals", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la listare");
    } finally {
      setLoading(false);
    }
  }, [filter, q, unused]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="referrals-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <FilterChip active={filter === ""} onClick={() => setFilter("")} label="Toate" testid="ref-filter-all" />
          <FilterChip active={filter === "eligible"} onClick={() => setFilter("eligible")} label="Beneficiu de dat" highlight testid="ref-filter-eligible" />
          <FilterChip active={filter === "active"} onClick={() => setFilter("active")} label="În derulare" testid="ref-filter-active" />
          <FilterChip active={filter === "delivered"} onClick={() => setFilter("delivered")} label="Beneficiu dat" testid="ref-filter-delivered" />
          <FilterChip active={filter === "expired"} onClick={() => setFilter("expired")} label="Expirate" testid="ref-filter-expired" />
          <label className="ml-1 inline-flex cursor-pointer items-center gap-1.5 text-xs text-[#6B7280]">
            <input type="checkbox" checked={unused} onChange={(e) => setUnused(e.target.checked)}
              className="h-3.5 w-3.5 accent-[#965EA7]" data-testid="ref-filter-unused" />
            Arată și codurile nefolosite
          </label>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Caută cod sau email…"
            className="pl-8 pr-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-full focus:ring-2 focus:ring-[#965EA7] outline-none w-64"
            data-testid="ref-search-input"
          />
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[#6B7280] inline-flex items-center gap-2 w-full justify-center">
            <Loader2 className="animate-spin" size={16} /> Se încarcă…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[#6B7280]" data-testid="ref-empty">
            <Gift size={36} className="mx-auto mb-3 text-[#965EA7]/30" />
            {filter
              ? `Niciun cod cu starea „${REFERRAL_STATUS[filter]?.label}”.`
              : unused ? "Niciun cod de recomandare încă." : "Nimeni nu s-a înscris încă printr-o recomandare."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F5F6F6] text-[#6B7280] text-xs uppercase tracking-widest">
              <tr>
                <Th>Cod</Th>
                <Th>Cine recomandă</Th>
                <Th>Stare</Th>
                <Th>Recomandări</Th>
                <Th>Ce i s-a promis</Th>
                <Th>Creat</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => {
                const meta = REFERRAL_STATUS[g.status] || { label: g.status, cls: "bg-[#EDEEF0] text-[#373A45]" };
                const progress = referralProgress(g);
                return (
                  <tr
                    key={g.id}
                    onClick={() => setDrawerId(g.id)}
                    className="border-t border-[#E5E7EB] cursor-pointer hover:bg-[#F5F6F6]/60"
                    data-testid={`ref-row-${g.id}`}
                  >
                    <Td><code className="font-mono font-bold text-[#965EA7]">{g.code}</code></Td>
                    <Td className="truncate">{g.owner_email}</Td>
                    <Td>
                      <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </Td>
                    <Td className="text-xs">
                      <strong className="text-[#519872]">{progress.paid}</strong>
                      <span className="text-[#9CA3AF]"> din {progress.required}</span> plătite
                      {progress.waiting > 0 && <span className="text-[#6B7280]"> · {progress.waiting} neplătite încă</span>}
                      {progress.refunded > 0 && (
                        <span className="ml-1 text-[#9b1f2c]" title="Plată rambursată ulterior">· {progress.refunded} rambursate</span>
                      )}
                    </Td>
                    <Td className="text-xs">{g.offer?.referrer_benefit || "—"}</Td>
                    <Td className="text-xs text-[#6B7280]">{(g.created_at || "").slice(0, 10)}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {total > items.length && (
          <div className="p-3 text-xs text-[#6B7280] text-center bg-[#F5F6F6]/40">
            Afișare {items.length} din {total}
          </div>
        )}
      </div>

      {drawerId && (
        <ReferralDrawer groupId={drawerId} canAct={!!canMutate} onChanged={load} onClose={() => setDrawerId(null)} />
      )}
    </div>
  );
}

/** A moment as the team reads it: local time, to the minute. */
function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
}

function FilterChip({ active, onClick, label, testid, highlight }) {
  let cls = "border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]";
  if (active) cls = "bg-[#373A45] text-white border-[#373A45]";
  else if (highlight) cls = "border-[#519872] text-[#1f6045] hover:bg-[#519872]/10";
  return (
    <button onClick={onClick} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${cls}`} data-testid={testid}>
      {label}
    </button>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 font-semibold">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function ReferralDrawer({ groupId, canAct, onChanged, onClose }) {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const reload = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const r = await api.get(`/admin/referrals/${groupId}`);
      setGroup(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Codul nu a putut fi încărcat."));
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { reload(); }, [reload]);

  // One of the team's actions, then the drawer and the list behind it again.
  const act = async (path, body, done) => {
    setBusy(true);
    try {
      await api.post(path, body || {});
      toast.success(done);
      await reload(true);
      onChanged?.();
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, "Acțiunea nu a reușit."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const deliver = () => {
    const said = window.prompt("Marchezi beneficiul ca acordat. Ce i-ai dat și cum? (opțional, rămâne în istoric)", "");
    if (said === null) return;
    act(`/admin/referrals/${groupId}/deliver`, { note: said }, "Beneficiul e marcat ca acordat.");
  };
  const cancelCode = () => {
    const why = window.prompt("Anulezi codul: nu mai poate fi folosit și beneficiul nu se mai acordă. Motivul:", "");
    if (why === null) return;
    if (why.trim().length < 3) { toast.error("Scrie motivul anulării."); return; }
    act(`/admin/referrals/${groupId}/cancel`, { reason: why }, "Codul a fost anulat.");
  };
  const reopen = () => {
    if (!window.confirm("Redeschizi codul? Revine la starea pe care o dau plățile recomandărilor.")) return;
    act(`/admin/referrals/${groupId}/reopen`, null, "Codul a fost redeschis.");
  };
  const unlink = (member) => {
    const who = `${member.enrollment?.prenume || ""} ${member.enrollment?.nume || ""}`.trim() || member.email;
    if (!window.confirm(`Scoți legătura dintre ${who} și acest cod? Înscrierea rămâne, dar nu mai contează ca recomandare.`)) return;
    act("/admin/referrals/unlink", { enrollment_id: member.enrollment_id }, "Legătura a fost scoasă.");
  };
  const saveNote = async () => {
    if (!note.trim()) return;
    if (await act(`/admin/referrals/${groupId}/notes`, { text: note }, "Nota a fost salvată.")) setNote("");
  };

  const copyLink = () => {
    if (!group?.code) return;
    const link = `${window.location.origin}/checkout?ref=${group.code}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success("Link copiat în clipboard"),
      () => toast.error("Nu am putut copia.")
    );
  };

  const meta = group ? (REFERRAL_STATUS[group.status] || { label: group.status, cls: "" }) : null;
  const progress = group ? referralProgress(group) : null;
  const offer = group?.offer || {};
  const can = actionsFor(group?.status);
  const names = { ...(group?.people || {}) };
  if (group) {
    const owner = group.owner_enrollment;
    if (owner) names[group.owner_enrollment_id] = `${owner.prenume || ""} ${owner.nume || ""}`.trim();
    (group.members || []).forEach((m) => {
      names[m.enrollment_id] = `${m.enrollment?.prenume || ""} ${m.enrollment?.nume || ""}`.trim() || m.email;
    });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose} data-testid="referral-drawer">
      <div className="bg-[#F5F6F6] w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold">Cod de recomandare</div>
            <h3 className="font-display text-lg font-bold text-[#373A45] truncate">
              {loading ? "Se încarcă…" : (
                <span className="inline-flex items-center gap-2">
                  <code className="font-mono text-[#965EA7]">{group?.code}</code>
                  {meta && (
                    <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${meta.cls}`}>
                      {meta.label}
                    </span>
                  )}
                </span>
              )}
            </h3>
          </div>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide"><X size={20} /></button>
        </header>

        {loading || !group ? (
          <div className="p-8 text-center text-[#6B7280]"><Loader2 className="animate-spin inline" /></div>
        ) : (
          <>
            <div className="bg-white border-b border-[#E5E7EB] px-6 flex gap-1">
              {[["details", "Detalii"], ["members", "Persoane"], ["timeline", "Istoric"]].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`px-3 py-2 text-xs font-semibold uppercase tracking-widest border-b-2 transition-colors ${tab === key ? "border-[#965EA7] text-[#965EA7]" : "border-transparent text-[#6B7280] hover:text-[#373A45]"}`}
                  data-testid={`ref-drawer-tab-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-5">
              {tab === "details" && (
                <>
                  <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold">Cod</div>
                        <code className="font-mono text-xl text-[#965EA7] font-bold">{group.code}</code>
                      </div>
                      <button onClick={copyLink} className="rounded-full px-3 py-1.5 text-xs font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] inline-flex items-center gap-1.5" data-testid="ref-copy-link">
                        <Copy size={12} /> Copiază linkul
                      </button>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <Row label="Cine recomandă" value={[names[group.owner_enrollment_id], group.owner_email].filter(Boolean).join(" · ")} />
                      <Row label="Stare" value={meta.label} />
                      <Row label="Recomandări plătite" value={`${progress.paid} din ${progress.required}`} />
                      <Row label="Înscriși cu acest cod" value={`${progress.entered}`} />
                      <Row label="Codul poate fi folosit până la" value={(group.expires_at || "").slice(0, 10)} />
                      {group.eligible_at && <Row label="Numărul a fost atins la" value={when(group.eligible_at)} />}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
                    <div className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold mb-3">Ce promite acest cod</div>
                    <div className="grid grid-cols-1 gap-3 text-sm">
                      <Row label="Cel care recomandă primește" value={offer.referrer_benefit} />
                      <Row label="Cel recomandat primește" value={offer.referred_benefit || "nimic"} />
                      <Row label="La câte persoane recomandate" value={`${progress.required}`} />
                    </div>
                    <p className="mt-3 text-xs text-[#6B7280]">
                      Este oferta din ziua în care codul a fost trimis. Nu se schimbă dacă modifici între timp setările.
                    </p>
                  </div>

                  {(group.delivered_at || group.cancelled_at) && (
                    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 text-sm text-[#373A45]" data-testid="ref-closed-box">
                      {group.delivered_at && (
                        <>Beneficiu acordat pe {when(group.delivered_at)}{group.delivered_by ? `, de ${group.delivered_by}` : ""}.</>
                      )}
                      {group.cancelled_at && (
                        <>
                          Cod anulat pe {when(group.cancelled_at)}{group.cancelled_by ? `, de ${group.cancelled_by}` : ""}.
                          {group.cancel_reason && <span className="block text-xs text-[#6B7280]">Motiv: {group.cancel_reason}</span>}
                        </>
                      )}
                    </div>
                  )}

                  {canAct && (can.deliver || can.cancel || can.reopen) && (
                    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="ref-actions">
                      <div className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold mb-3">Acțiuni</div>
                      {progress.refunded > 0 && can.deliver && (
                        <p className="mb-3 text-xs font-semibold text-[#9b1f2c]" data-testid="ref-refund-warning">
                          {progress.refunded === 1 ? "O plată a fost rambursată" : `${progress.refunded} plăți au fost rambursate`} după
                          ce a contat. Verifică la „Persoane” înainte de a acorda beneficiul.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {can.deliver && (
                          <button type="button" onClick={deliver} disabled={busy}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#519872] px-4 py-2 text-sm font-semibold text-white hover:bg-[#3f7a5a] disabled:opacity-50"
                            data-testid="ref-action-deliver">
                            <CheckCircle2 size={14} /> Marchează beneficiul ca acordat
                          </button>
                        )}
                        {can.cancel && (
                          <button type="button" onClick={cancelCode} disabled={busy}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#9b1f2c] px-4 py-2 text-sm font-semibold text-[#9b1f2c] hover:bg-[#FDECEC] disabled:opacity-50"
                            data-testid="ref-action-cancel">
                            <Ban size={14} /> Anulează codul
                          </button>
                        )}
                        {can.reopen && (
                          <button type="button" onClick={reopen} disabled={busy}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#373A45] px-4 py-2 text-sm font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-50"
                            data-testid="ref-action-reopen">
                            <RotateCcw size={14} /> Redeschide codul
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="ref-notes">
                    <div className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold mb-3 inline-flex items-center gap-1.5">
                      <StickyNote size={13} /> Note
                    </div>
                    {(group.notes || []).length === 0 && <p className="text-xs text-[#6B7280]">Nicio notă.</p>}
                    <ul className="space-y-2">
                      {(group.notes || []).slice().reverse().map((n, i) => (
                        <li key={n.id || i} className="rounded-xl bg-[#F5F6F6] px-3 py-2 text-sm text-[#373A45]" data-testid="ref-note">
                          {n.text || n.note || String(n)}
                          <span className="block text-[10px] text-[#6B7280]">
                            {when(n.ts)}{n.by ? ` · ${n.by}` : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {canAct && (
                      <div className="mt-3">
                        <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} rows={2}
                          placeholder="Adaugă o notă pentru echipă…"
                          className="w-full rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2 text-sm text-[#373A45] outline-none focus:ring-2 focus:ring-[#965EA7]"
                          data-testid="ref-note-input" />
                        <button type="button" onClick={saveNote} disabled={busy || !note.trim()}
                          className="mt-1 rounded-full bg-[#965EA7] px-3 py-1 text-xs font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50"
                          data-testid="ref-note-save">
                          Salvează nota
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {tab === "members" && (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F5F6F6] text-[#6B7280] text-xs uppercase tracking-widest">
                      <tr>
                        <Th>Rol</Th>
                        <Th>Nume</Th>
                        <Th>Email</Th>
                        <Th>Plată</Th>
                        {canAct && <Th> </Th>}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-[#E5E7EB]">
                        <Td><span className="text-[10px] font-bold uppercase tracking-widest bg-[#965EA7]/15 text-[#7A4986] rounded-full px-2 py-0.5">Recomandă</span></Td>
                        <Td>{group.owner_enrollment?.prenume} {group.owner_enrollment?.nume}</Td>
                        <Td className="truncate">{group.owner_email}</Td>
                        <Td><span className="text-[#1f6045] inline-flex items-center gap-1 text-xs"><CheckCircle2 size={12} /> plătit</span></Td>
                        {canAct && <Td> </Td>}
                      </tr>
                      {(group.members || []).map((m, i) => (
                        <tr key={m.enrollment_id || i} className="border-t border-[#E5E7EB]" data-testid="ref-member-row">
                          <Td>
                            <span className="text-[10px] font-bold uppercase tracking-widest bg-[#EDEEF0] text-[#373A45] rounded-full px-2 py-0.5">Recomandat</span>
                            {m.manual && <span className="mt-1 block text-[10px] text-[#6B7280]" title={m.linked_by || ""}>legat de echipă</span>}
                          </Td>
                          <Td>{m.enrollment?.prenume} {m.enrollment?.nume}</Td>
                          <Td className="truncate">{m.email}</Td>
                          <Td>
                            {m.is_valid ? (
                              <span className="text-[#1f6045] inline-flex items-center gap-1 text-xs">
                                <CheckCircle2 size={12} /> plătit
                                {m.refunded_at && <span className="ml-1 rounded-full bg-[#FDECEC] px-1.5 py-0.5 text-[10px] font-semibold text-[#9b1f2c]">plată rambursată ulterior</span>}
                              </span>
                            ) : (
                              <span className="text-[#6B7280] inline-flex items-center gap-1 text-xs"><Clock size={12} /> neplătit încă</span>
                            )}
                          </Td>
                          {canAct && (
                            <Td>
                              <button type="button" onClick={() => unlink(m)} disabled={busy}
                                className="text-xs text-[#9b1f2c] underline disabled:opacity-50" data-testid="ref-member-unlink">
                                Scoate legătura
                              </button>
                            </Td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === "timeline" && (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
                  <ol className="space-y-3">
                    {(group.events || []).slice().reverse().map((e) => {
                      const line = eventLine(e, names);
                      return (
                        <li key={e.id} className="flex gap-3 text-sm" data-testid="ref-event">
                          <div className="text-[10px] text-[#6B7280] w-32 shrink-0 font-mono">
                            {when(e.ts)}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-[#373A45]">{line.title}</div>
                            {(line.detail || line.by) && (
                              <div className="text-xs text-[#6B7280]">
                                {[line.detail, line.by && `de ${line.by}`].filter(Boolean).join(" · ")}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold">{label}</div>
      <div className="text-sm text-[#373A45] break-words">{value || "—"}</div>
    </div>
  );
}
